import { createContext, useCallback, useContext, useEffect, useState } from 'react'

import api from '@/lib/api/client'

import { QuickStudyCutPlotsCut, QuickStudyCutPlotsQuestion, QuickStudyCutPlotsView } from '@digital-twins/shared-types'
import { Result } from './quickStudyTypes'

/**
 * Compare-segments study plots for a quick study (W6-QS-c, owner option B).
 *
 * When a reader picks cuts, the card asks Nest (`POST /workflow-runs/:token/cut-plots`)
 * for every read's study plots on the whole run and on each cut, drawn by Python
 * through the producers the card's own plots come from. Python owns every number;
 * this only transports the request. One request per run revision and pick serves
 * every card (a bounded module cache), and a failed request is dropped from the
 * cache so the next pick asks again. A read stored before the cut plots existed (no
 * `plot_inputs`) never asks: the card keeps its legacy cut readers.
 */

/**
 * The run whose reads a card can ask cut plots for, and its revision (the latest
 * `updatedAt` of its steps): a rerun or a regenerated read is a new revision, so
 * the browser never keeps drawing a view of the reads it replaced.
 */
export interface QuickStudyCutPlotsSource {
  runToken: string
  revision?: string
}

export const QuickStudyCutPlotsContext = createContext<QuickStudyCutPlotsSource | null>(null)

/** The cut plots source of a run the thread holds (null with no run). */
export function quickStudyCutPlotsSourceFor(
  run: { token: string; steps?: Array<{ updatedAt?: string | null }> } | null | undefined,
): QuickStudyCutPlotsSource | null {
  if (!run) {
    return null
  }
  const revision = (run.steps ?? []).reduce(
    (latest, step) => (step.updatedAt && step.updatedAt > latest ? step.updatedAt : latest),
    '',
  )
  return { runToken: run.token, revision }
}

export type QuickStudyCutPlotsStatus = 'unavailable' | 'loading' | 'ready' | 'failed'

export interface QuickStudyCutPlotsState {
  status: QuickStudyCutPlotsStatus
  /** The card's read, when the endpoint answered it (`ok` or `refused`). */
  question: QuickStudyCutPlotsQuestion | null
  /**
   * W7 Q10: ask again for this pick, dropping what the browser holds for it (a failure,
   * a refusal or an answer the card cannot draw). A new read shows its error with this
   * retry instead of falling back to browser bands.
   */
  retry?: () => void
}

/** The id a read is addressed by: the living FactBundle's, else `study_doc_plot_id`. */
export function quickStudyQuestionId(
  result: Pick<Result, 'fact_bundle' | 'study_doc_plot_id'>,
): string | null {
  const fromBundle = result.fact_bundle?.question_id?.trim()
  if (fromBundle) {
    return fromBundle
  }
  return result.study_doc_plot_id?.trim() || null
}

export async function fetchQuickStudyCutPlots(
  runToken: string,
  cuts: Array<QuickStudyCutPlotsCut>,
): Promise<QuickStudyCutPlotsView> {
  const response = await api.post<QuickStudyCutPlotsView>(
    `/workflow-runs/${encodeURIComponent(runToken)}/cut-plots`,
    { cuts },
  )
  return response.data
}

/** One request per run revision and pick, shared by every card. */
interface CachedPick {
  pending: Promise<QuickStudyCutPlotsView>
  /** The answer once it arrived: a card reads it synchronously, with no loading frame. */
  view: QuickStudyCutPlotsView | null
  /** Already asked again because a card's read was missing from the answer. */
  hasRefetched: boolean
}

/** Picks kept at once; the oldest goes first. */
export const MAX_CACHED_CUT_PLOT_PICKS = 24

const picks = new Map<string, CachedPick>()

function pickKeyOf(source: QuickStudyCutPlotsSource, cuts: Array<QuickStudyCutPlotsCut>): string {
  return JSON.stringify([source.runToken, source.revision ?? '', cuts])
}

/** Ask (or ask again) for a pick; a failure is dropped so the next pick asks again. */
function requestPick(
  key: string,
  runToken: string,
  cuts: Array<QuickStudyCutPlotsCut>,
  hasRefetched: boolean,
): CachedPick {
  const entry: CachedPick = {
    pending: Promise.resolve({ version: '', questions: [] }),
    view: null,
    hasRefetched,
  }
  entry.pending = fetchQuickStudyCutPlots(runToken, cuts).then(
    (view) => {
      entry.view = view
      return view
    },
    (error: unknown) => {
      if (picks.get(key) === entry) {
        picks.delete(key)
      }
      throw error
    },
  )
  picks.delete(key)
  picks.set(key, entry)
  while (picks.size > MAX_CACHED_CUT_PLOT_PICKS) {
    const oldest = picks.keys().next().value
    if (oldest === undefined) {
      break
    }
    picks.delete(oldest)
  }
  return entry
}

/** Test seam: forget every cached pick. */
export function clearQuickStudyCutPlotsCache(): void {
  picks.clear()
}

/** Test seam: how many picks the browser holds. */
export function cachedQuickStudyCutPlotPickCount(): number {
  return picks.size
}

const UNAVAILABLE: QuickStudyCutPlotsState = { status: 'unavailable', question: null }
const LOADING: QuickStudyCutPlotsState = { status: 'loading', question: null }
const FAILED: QuickStudyCutPlotsState = { status: 'failed', question: null }

/** A card's state off a pick: ready with its read, loading while asked, failed once missing twice. */
function stateOf(entry: CachedPick | undefined, questionId: string): QuickStudyCutPlotsState | null {
  if (!entry?.view) {
    return null
  }
  const question = entry.view.questions.find((item) => item.questionId === questionId) ?? null
  if (question) {
    return { status: 'ready', question }
  }
  return entry.hasRefetched ? FAILED : LOADING
}

/**
 * The cut plots of this card's read for the picked cuts. `unavailable` with no
 * cut, no run in context, no address or no `plot_inputs` (a stored read): the
 * caller keeps its legacy cut readers. A new read that `failed`, was refused or
 * cannot be drawn shows an error with `retry` (W7 Q10), never browser bands.
 * A pick another card already resolved reads synchronously (no loading frame);
 * `loading` means a request is really in flight. A ready answer missing this
 * card's read (a read added since) is dropped and asked again, once.
 */
export function useQuickStudyCutPlots(
  result: Pick<Result, 'fact_bundle' | 'study_doc_plot_id' | 'plot_inputs'>,
  cuts: Array<QuickStudyCutPlotsCut>,
): QuickStudyCutPlotsState {
  const source = useContext(QuickStudyCutPlotsContext)
  const questionId = quickStudyQuestionId(result)
  const isAskable = Boolean(source && questionId && result.plot_inputs && cuts.length > 0)
  const key = isAskable && source ? pickKeyOf(source, cuts) : ''
  const runToken = source?.runToken ?? ''

  // Bumped when a request settles, so the card re-reads the cache.
  const [, setSettled] = useState(0)
  const [failedKey, setFailedKey] = useState<string | null>(null)
  // Bumped by `retry`, so the effect asks again for the same pick.
  const [attempt, setAttempt] = useState(0)

  const retry = useCallback(() => {
    if (!key) {
      return
    }
    picks.delete(key)
    setFailedKey(null)
    setAttempt((count) => count + 1)
  }, [key])

  // Read on every render: an evicted pick re-runs the effect, which asks again.
  const cached = key ? picks.get(key) : undefined

  useEffect(() => {
    if (!key || !questionId) {
      return
    }
    let isCurrent = true
    // Fresh from the cache: another card may have asked since this render.
    let entry = picks.get(key)
    if (entry?.view) {
      const isAnswered = entry.view.questions.some((item) => item.questionId === questionId)
      if (isAnswered || entry.hasRefetched) {
        return
      }
      entry = requestPick(key, runToken, JSON.parse(key)[2] as Array<QuickStudyCutPlotsCut>, true)
      setSettled((count) => count + 1)
    } else if (!entry) {
      entry = requestPick(key, runToken, JSON.parse(key)[2] as Array<QuickStudyCutPlotsCut>, false)
      setFailedKey(null)
    }
    entry.pending.then(
      () => {
        if (isCurrent) {
          setSettled((count) => count + 1)
        }
      },
      () => {
        if (isCurrent) {
          setFailedKey(key)
        }
      },
    )
    return () => {
      isCurrent = false
    }
  }, [key, questionId, runToken, cached, attempt])

  if (!isAskable || !questionId) {
    return UNAVAILABLE
  }
  const resolved = stateOf(cached, questionId)
  if (resolved) {
    return resolved
  }
  // Only a request that failed can be asked again; a refusal is the answer.
  if (!cached && failedKey === key) {
    return { ...FAILED, retry }
  }
  return LOADING
}
